import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { UnitToggle, TextField, NotesField, u } from "./_shared";
import { CASING_STRING_KINDS } from "@/lib/profiles/schemas";
import TubularLibraryField from "@/components/TubularLibraryField";
import { Field, TextInput } from "@/components/ReportPrimitives";

// CasingProfileForm — string-by-string OD/ID with Reference-Library pickers.
//
// Shoe MD / TVD live on the Well Profile (single source of truth). The
// previously-shown `From MD` / `To MD` columns were vestigial from an older
// tally model — they were never consumed by the kill sheet engine and were
// removed in the Acceptance Pass to reduce manual entry.

const _emptyString = () => ({
  kind: "Surface", odIn: "", idIn: "", weightLbFt: "", grade: "", libId: "",
});

export default function CasingProfileForm({ value, onChange }) {
  const units = value.units || "IMP";
  const set = (patch) => onChange({ ...value, ...patch });
  const setStrings = (strings) => set({ strings });
  const updateRow = (i, patch) =>
    setStrings(value.strings.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const addRow = () => setStrings([...(value.strings || []), _emptyString()]);
  const removeRow = (i) =>
    setStrings(value.strings.length <= 1
      ? value.strings
      : value.strings.filter((_, idx) => idx !== i));

  return (
    <div className="flex flex-col gap-4" data-testid="casing-profile-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <TextField value={value} onChange={onChange} field="label" label="Profile label"
                   placeholder="e.g. Phase 2 — 9⅝ intermediate"
                   testId="casing-label" />
        <TextField value={value} onChange={onChange} field="rig" label="Rig (optional)"
                   placeholder="e.g. Rig Alpha" testId="casing-rig" />
        <UnitToggle value={units} onChange={(v) => set({ units: v })} testId="casing-units" />
      </div>

      <fieldset className="border-t border-[var(--rs-border)] pt-3">
        <legend className="text-[10px] font-mono uppercase tracking-wider text-[var(--rs-text-3)] px-2">
          Casing strings (surface → bottom)
        </legend>
        <p className="text-[10px] text-[var(--rs-text-3)] mb-2 leading-snug">
          Pick a casing spec from the Reference Library to auto-fill OD / ID
          (and grade where available). Shoe depths come from the Well Profile.
        </p>
        <div className="flex flex-col gap-3">
          {(value.strings || []).map((s, i) => (
            <CasingRow key={i} index={i} string={s} units={units}
                       onChange={(patch) => updateRow(i, patch)}
                       onRemove={() => removeRow(i)}
                       canRemove={value.strings.length > 1} />
          ))}
        </div>
        <button type="button" onClick={addRow} data-testid="casing-add-row"
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px]"
                style={{
                  background: "rgba(245,180,0,0.10)",
                  color: "var(--rs-amber)",
                  border: "1px solid rgba(245,180,0,0.35)",
                  minHeight: 32,
                }}>
          <Plus className="w-4 h-4" /> Add string
        </button>
      </fieldset>

      <NotesField value={value} onChange={onChange} testId="casing-notes" />
    </div>
  );
}

function CasingRow({ index, string: s, units, onChange, onRemove, canRemove }) {
  const setOdLib = ({ value, src }) => onChange({ odIn: value, libId: src?.libId || "", libVer: src?.libVer || "" });
  const setOd    = (v) => onChange({ odIn: v, libId: "" });
  const setIdLib = ({ value, src }) => onChange({ idIn: value, libId: src?.libId || s.libId || "", libVer: src?.libVer || s.libVer || "" });
  const setId    = (v) => onChange({ idIn: v });

  return (
    <div className="card-rs p-3" data-testid={`casing-row-${index}`}
         style={{ background: "rgba(22,24,29,0.4)" }}>
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
        <div className="sm:col-span-3">
          <Field label="Kind">
            <select data-testid={`casing-row-${index}-kind`} value={s.kind}
                    onChange={(e) => onChange({ kind: e.target.value })}
                    className="input-rs text-[12px] w-full" style={{ height: 36 }}>
              {CASING_STRING_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </Field>
        </div>
        <div className="sm:col-span-3">
          <TubularLibraryField
            label={`OD (${u.dia(units)})`}
            kind="casing"
            pick="od_in"
            value={s.odIn}
            src={s.libId ? { libId: s.libId, libVer: s.libVer } : null}
            onValueChange={setOd}
            onPick={setOdLib}
            testId={`casing-row-${index}-odIn`}
          />
        </div>
        <div className="sm:col-span-3">
          <TubularLibraryField
            label={`ID (${u.dia(units)})`}
            kind="casing"
            pick="id_in"
            value={s.idIn}
            src={s.libId ? { libId: s.libId, libVer: s.libVer } : null}
            onValueChange={setId}
            onPick={setIdLib}
            testId={`casing-row-${index}-idIn`}
          />
        </div>
        <div className="sm:col-span-2">
          <Field label="Weight (lb/ft)">
            <TextInput type="number" value={s.weightLbFt ?? ""}
                       onChange={(v) => onChange({ weightLbFt: v })}
                       testId={`casing-row-${index}-weight`} />
          </Field>
        </div>
        <div className="sm:col-span-1">
          <Field label="Grade">
            <TextInput value={s.grade ?? ""} onChange={(v) => onChange({ grade: v })}
                       testId={`casing-row-${index}-grade`} />
          </Field>
        </div>
      </div>
      {canRemove && (
        <div className="flex justify-end mt-2">
          <button type="button" onClick={onRemove}
                  data-testid={`casing-row-${index}-remove`}
                  className="p-1.5 rounded text-[var(--rs-text-3)] hover:text-[#FCA5A5]"
                  title="Remove string">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
